import { Stack, SxProps, useTheme } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { alertIcon, mailIcon, phoneIcon, userIcon } from './icons';
import { LeadFormInput } from './lead-form-input';
import { ConsentCheckbox } from './consent-checkbox';
import { useCallback } from 'react';
import { useIsMobile } from '../../hooks';

export type LeadFormInputsProps = {
  sx?: SxProps;
  disabled?: boolean;
  setName: (value: string) => void;
  name: string;
  setPhone: (value: string) => void;
  phone: string;
  setEmail: (value: string) => void;
  email: string;
  errors: {
    name?: string;
    phone?: string;
    email?: string;
    consent?: string;
  };
  handleRTLPhoneChange: (value: string) => void;
  t: (key: string) => string;
  showConsentCheckbox?: boolean;
  consentChecked?: boolean;
  onConsentChange?: (checked: boolean) => void;
};

export function LeadFormInputs({
  sx,
  disabled,
  setName,
  name,
  errors,
  setPhone,
  phone,
  setEmail,
  email,
  handleRTLPhoneChange,
  t,
  showConsentCheckbox = false,
  consentChecked = false,
  onConsentChange,
}: LeadFormInputsProps) {
  const { direction } = useTheme();
  const isMobile = useIsMobile();

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (isMobile) {
        setTimeout(() => {
          event.target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 300); // Wait for keyboard to show up
      }
    },
    [isMobile],
  );

  return (
    <Stack sx={sx}>
      <LeadFormInput
        placeholder={t('labels.yourName')}
        icon={userIcon}
        value={name}
        onChange={(e) => setName(e.target.value)}
        helperText={errors?.name}
        error={!!errors?.name}
        disabled={disabled}
        onFocus={handleFocus}
      />
      {direction === 'rtl' ? (
        <>
          <LeadFormInput
            placeholder={t('labels.phoneNumber')}
            icon={phoneIcon}
            value={phone}
            onChange={(e) => handleRTLPhoneChange(e.target.value)}
            helperText={errors?.phone}
            error={!!errors?.phone}
            disabled={disabled}
            onFocus={handleFocus}
          />
        </>
      ) : (
        <>
          <MuiTelInput
            placeholder={t('labels.phoneNumber')}
            value={phone}
            onChange={(value) => setPhone(value)}
            helperText={errors?.phone}
            disabled={disabled}
            error={!!errors?.phone}
            size="small"
            onFocus={handleFocus}
            data-treedistestid="input-phone-number-field"
            slotProps={{
              input: {
                startAdornment: phoneIcon,
                endAdornment: errors?.phone ? alertIcon : null,
              },
            }}
            sx={{
              '& input[type="tel"]': {
                // textAlign: direction ==='rtl' ? 'end' : 'start',
                paddingInlineStart: '10px',
                paddingInlineEnd: '10px',
              },
              '&>.MuiInputBase-root': {
                height: '44px',
                fontSize: '1rem',
                '& svg': {
                  color: 'grey.500',
                },
              },
              '.MuiTelInput-FlagImg': {
                display: 'none',
              },
              '.MuiFormHelperText-root': {
                marginLeft: '3px',
                marginRight: '3px',
              },
            }}
            disableDropdown
            fullWidth
          />
        </>
      )}
      <LeadFormInput
        placeholder={t('labels.emailAddress')}
        icon={mailIcon}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        helperText={errors?.email}
        error={!!errors?.email}
        disabled={disabled}
        autoComplete="email"
        onFocus={handleFocus}
      />
      {showConsentCheckbox && onConsentChange && (
        <ConsentCheckbox
          checked={consentChecked}
          onChange={onConsentChange}
          disabled={disabled}
          error={errors?.consent}
          t={t}
        />
      )}
    </Stack>
  );
}
